import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { EvidenceFocus } from "../../../../annotation";
import type { StatementCardShape, StatementReferences, ValueReadings } from "../../../../knowledgeData";
import type { TermCards } from "../../../../ontology";
import { chipClassName } from "../../../../../ui/styles/chipStyles";
import PartStatements from "../PartStatements";
import type { PlacedStatement } from "../regestParts";
import TextFocusButton from "../TextFocusButton";
import { withoutPremiseRows, type Conclusion, type ConclusionShape } from "./regestConclusions";

interface ConclusionsSectionProps {
  readonly conclusions: readonly Conclusion[];
  readonly shape: ConclusionShape;
  /** "#n kinds" by statement key, as the statements' words carry it in the text. */
  readonly labelOf: (statementKey: string) => string;
  readonly numbers: ReadonlyMap<string, number>;
  readonly cards: ReadonlyMap<string, TermCards | null>;
  readonly focus: EvidenceFocus;
  readonly askInProject: string | undefined;
  readonly extra: ((statement: PlacedStatement) => ReactNode) | undefined;
  /** How the project's profile reads one statement (Daniel's card). */
  readonly statementShape: StatementCardShape | null;
  /** How dates and amounts read, the same as beside the text. */
  readonly readings: ValueReadings | undefined;
  /** The regest's statements by what each is about, so a conclusion's value naming one reads as it. */
  readonly references?: StatementReferences;
}

/**
 * The statements concluded from others (Daniel's "Schlussfolgerungen", *erschlossen*), after the text:
 * each read as any statement is, with the statements it rests on, which follow into the text. None,
 * no section, as in his viewer.
 */
export default function ConclusionsSection({ conclusions, shape, labelOf, numbers, cards, focus, askInProject, extra, statementShape, readings, references }: ConclusionsSectionProps) {
  const { t } = useTranslation();
  if (conclusions.length === 0) return null;
  const premisesOf = new Map(conclusions.map((conclusion) => [conclusion.line.shown.key, conclusion.premises]));
  const basedOn = (statement: PlacedStatement) => (
    <>
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 text-sm">
        <dt className="app-text-muted">{t("sources.document.conclusions.basedOn")}</dt>
        <dd className="flex min-w-0 flex-wrap gap-1">
          {(premisesOf.get(statement.key) ?? []).map((premise) => (
            <TextFocusButton key={premise} statementKey={premise} focus={focus} className={chipClassName()}>{labelOf(premise)}</TextFocusButton>
          ))}
        </dd>
      </dl>
      {extra?.(statement)}
    </>
  );
  return (
    <section id="regest-conclusions" aria-labelledby="regest-conclusions-title" className="space-y-1 py-3">
      <h4 id="regest-conclusions-title" className="app-label text-xs">{t("sources.document.conclusions.title")}</h4>
      <PartStatements
        items={conclusions.map((conclusion) => withoutPremiseRows(conclusion.line, shape))}
        cards={cards}
        focus={focus}
        askInProject={askInProject}
        extra={basedOn}
        numbers={numbers}
        shape={statementShape}
        readings={readings}
        references={references}
      />
    </section>
  );
}
